import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Loader2,
  X,
  ArrowRight,
  Table2,
  Plus,
  Trash2,
  Sparkles,
  CheckCircle2,
  Eye,
  FileText as FileTextIcon,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { Button } from "../ui";
import { useToast } from "../ui/toast";

interface Column {
  name: string;
  type: "text" | "number" | "date" | "bool" | "enum" | "list";
  description: string;
}

interface SuggestedIntent {
  label: string;
  granularity: string;
  columns: Column[];
}

interface DocFull {
  id: string;
  title: string;
  project_id: string | null;
  summary: string | null;
  summary_status: string;
  suggested_schemas: SuggestedIntent[] | null;
  intent_status: string;
  extract_strategy: "text" | "vision";
}

interface DatasetOut {
  id: string;
  name: string;
  status: string;
  last_error?: string | null;
  columns: Column[];
  row_count: number;
}

interface InferResp {
  columns: Column[];
}

type Step = "intent" | "prompt" | "schema" | "running" | "done";
type Strategy = "text" | "vision";

const TYPES: Column["type"][] = ["text", "number", "date", "bool", "enum", "list"];

export default function DatasetWizard({
  open,
  onClose,
  doc,
}: {
  open: boolean;
  onClose: () => void;
  doc: { id: string; title: string; project_id: string | null } | null;
}) {
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("intent");
  const [hint, setHint] = useState("");
  const [dsId, setDsId] = useState<string | null>(null);
  const [cols, setCols] = useState<Column[]>([]);
  const [busy, setBusy] = useState(false);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [docFull, setDocFull] = useState<DocFull | null>(null);
  const [strategy, setStrategy] = useState<Strategy>("text");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Polling immer stoppen wenn die Komponente unmountet.
  useEffect(() => stopPolling, []);

  useEffect(() => {
    if (!open || !doc) return;
    setStep("intent");
    setHint("");
    setDsId(null);
    setCols([]);
    setBusy(false);
    setRowCount(null);
    setRunError(null);
    stopPolling();
    // Doc-Detail laden (für suggested_schemas + extract_strategy)
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<DocFull>(`/documents/${doc.id}`);
        if (cancelled) return;
        setDocFull(d);
        setStrategy(d.extract_strategy ?? "text");
        // Wenn keine Vorschläge da sind → Step 0 überspringen
        if (!d.suggested_schemas || d.suggested_schemas.length === 0) {
          setStep("prompt");
        }
      } catch {
        if (!cancelled) setStep("prompt");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, doc]);

  if (!doc) return null;

  const useIntent = (intent: SuggestedIntent) => {
    setCols(intent.columns);
    setHint(intent.granularity);
    // direkt zum Schema-Edit-Step
    setStep("schema");
  };

  const skipIntent = () => {
    setStep("prompt");
  };

  const regenIntent = async () => {
    if (!docFull) return;
    setBusy(true);
    try {
      const r = await api.post<{ intents: SuggestedIntent[]; count: number }>(
        `/documents/${docFull.id}/intent`,
        {}
      );
      setDocFull({ ...docFull, suggested_schemas: r.intents, intent_status: "done" });
      if (r.count === 0) {
        toast.err("Konnte keine sinnvollen Schemata generieren");
        setStep("prompt");
      } else {
        toast.ok(`${r.count} Vorschlag${r.count === 1 ? "" : "e"} bereit`);
        setStep("intent");
      }
    } catch (e) {
      toast.err("Re-Generieren fehlgeschlagen", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const inferSchema = async () => {
    if (!hint.trim()) {
      toast.err("Bitte beschreibe, was die Tabelle enthalten soll");
      return;
    }
    setBusy(true);
    try {
      const ds = await api.post<DatasetOut>("/datasets", {
        name: `Aus: ${doc.title}`,
        source_type: "documents",
        source_config: { document_ids: [doc.id], strategy },
        columns: [],
        project_id: doc.project_id,
      });
      setDsId(ds.id);
      const proposal = await api.post<InferResp>(
        `/datasets/${ds.id}/infer-schema`,
        { hint: hint.trim() }
      );
      setCols(proposal.columns ?? []);
      setStep("schema");
    } catch (e) {
      toast.err("Schema-Vorschlag fehlgeschlagen", (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const updateCol = (idx: number, patch: Partial<Column>) => {
    setCols((c) => c.map((col, i) => (i === idx ? { ...col, ...patch } : col)));
  };
  const removeCol = (idx: number) => {
    setCols((c) => c.filter((_, i) => i !== idx));
  };
  const addCol = () => {
    setCols((c) => [...c, { name: "", type: "text", description: "" }]);
  };

  // /run ist seit Phase 2 asynchron (202 {status:"queued"}) — der Worker
  // extrahiert im Hintergrund. Wir pollen GET /datasets/{id} bis ready/error.
  const pollDataset = (id: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const d = await api.get<DatasetOut>(`/datasets/${id}`);
        if (d.status === "ready") {
          stopPolling();
          setRowCount(d.row_count);
          setStep("done");
          setBusy(false);
        } else if (d.status === "error") {
          stopPolling();
          const msg = d.last_error ?? "Unbekannter Fehler";
          setRunError(msg);
          toast.err("Extraktion fehlgeschlagen", msg);
          setStep("schema");
          setBusy(false);
        }
        // queued/extracting → weiter pollen
      } catch {
        // Transienter Netzwerkfehler → beim nächsten Intervall erneut versuchen.
      }
    }, 2500);
  };

  const runExtraction = async () => {
    const valid = cols.filter((c) => c.name.trim().length > 0);
    if (valid.length === 0) {
      toast.err("Mindestens eine Spalte mit Namen");
      return;
    }
    setBusy(true);
    setRunError(null);
    setStep("running");
    try {
      let currentDsId = dsId;
      // Wenn aus dem Intent-Step gekommen: Dataset noch nicht angelegt
      if (!currentDsId) {
        const ds = await api.post<DatasetOut>("/datasets", {
          name: `Aus: ${doc.title}`,
          source_type: "documents",
          source_config: { document_ids: [doc.id], strategy },
          columns: [],
          project_id: doc.project_id,
        });
        currentDsId = ds.id;
        setDsId(currentDsId);
      } else {
        // Strategie ggf. aktualisieren
        await api.patch(`/datasets/${currentDsId}`, {
          source_config: { document_ids: [doc.id], strategy },
        });
      }
      await api.patch(`/datasets/${currentDsId}`, { columns: valid });
      // 202 queued — kein synchrones Ergebnis mehr; ab jetzt pollen.
      await api.post(`/datasets/${currentDsId}/run`, {});
      pollDataset(currentDsId);
    } catch (e) {
      toast.err("Extraktion fehlgeschlagen", (e as Error).message);
      setStep("schema");
      setBusy(false);
    }
  };

  const goToDataset = () => {
    onClose();
    if (dsId) navigate(`/ds/${dsId}`);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[680px] max-w-[92vw] max-h-[88vh] flex flex-col animate-modal-in"
        >
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Table2 className="w-4 h-4 text-brand-600" />
            <Dialog.Title className="font-semibold text-sm flex-1">
              Tabelle aus „{doc.title}" erstellen
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 text-text-3 hover:text-text">
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <Stepper step={step} />

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {step === "intent" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium flex items-center gap-1.5 flex-1">
                    <Sparkles className="w-4 h-4 text-brand-600" />
                    KI-Vorschläge — wähle eine Tabellen-Struktur
                  </div>
                  <button
                    onClick={regenIntent}
                    disabled={busy}
                    className="text-[11px] text-brand-600 hover:underline disabled:opacity-40 inline-flex items-center gap-1"
                    title="Vorschläge neu generieren"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Neu generieren
                  </button>
                </div>
                {docFull?.intent_status === "pending" ? (
                  <div className="text-xs text-text-2 italic inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Vorschläge
                    werden generiert …
                  </div>
                ) : null}
                <div className="space-y-2">
                  {(docFull?.suggested_schemas ?? []).map((intent, idx) => (
                    <button
                      key={idx}
                      onClick={() => useIntent(intent)}
                      className="w-full text-left bg-surface-2 border border-border rounded-md p-3 hover:border-brand-500/60 hover:bg-brand-50/40 dark:hover:bg-brand-900/20 transition-colors group"
                    >
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium flex-1 group-hover:text-brand-600">
                          {intent.label}
                        </div>
                        <span className="text-[11px] text-text-3">
                          {intent.columns.length} Spalten
                        </span>
                      </div>
                      <div className="text-xs text-text-2 mt-0.5">
                        {intent.granularity}
                      </div>
                      <div className="text-[11px] text-text-3 mt-1 truncate">
                        {intent.columns.map((c) => c.name).join(" · ")}
                      </div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={skipIntent}
                  className="text-xs text-text-2 hover:text-brand-600 underline"
                >
                  Stattdessen selbst beschreiben →
                </button>
              </div>
            )}

            {step === "prompt" && (
              <div className="space-y-3">
                {docFull?.summary && (
                  <div className="bg-surface-2 border border-border rounded-md p-3 text-xs">
                    <div className="font-semibold text-text-2 uppercase tracking-wider text-[10px] mb-1">
                      Worum geht es im Dokument
                    </div>
                    <div className="text-text leading-snug">
                      {docFull.summary}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium flex-1">
                    Was soll die Tabelle enthalten?
                  </label>
                  {docFull && (
                    <button
                      onClick={regenIntent}
                      disabled={busy}
                      className="text-[11px] text-brand-600 hover:underline disabled:opacity-40 inline-flex items-center gap-1"
                      title="KI-Vorschläge generieren"
                    >
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      Vorschläge generieren
                    </button>
                  )}
                </div>
                <textarea
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  rows={4}
                  placeholder={`z.B. "Eine Zeile pro Studie, mit Methode, n, Outcome, Limitation."`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      inferSchema();
                    }
                  }}
                  className="w-full text-sm bg-surface-2 border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500/40 resize-y"
                />
                <p className="text-[11px] text-text-2">
                  Max. 8 Spalten, danach editierbar.
                </p>
              </div>
            )}

            {step === "schema" && (
              <div className="space-y-3">
                {runError && (
                  <div className="bg-danger-soft border border-danger/40 rounded-md p-2.5 text-xs text-danger">
                    <span className="font-medium">Extraktion fehlgeschlagen:</span>{" "}
                    {runError}
                  </div>
                )}
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-brand-600" />
                  Spalten — bearbeiten oder bestätigen
                </div>
                <div className="bg-surface-2 border border-border rounded-md p-2 flex items-center gap-2 text-xs">
                  <span className="text-text-2">Extraktion:</span>
                  <button
                    type="button"
                    onClick={() => setStrategy("text")}
                    className={clsx(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-md",
                      strategy === "text"
                        ? "bg-surface shadow-sm font-medium"
                        : "text-text-2 hover:text-text"
                    )}
                  >
                    <FileTextIcon className="w-3 h-3" /> Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setStrategy("vision")}
                    className={clsx(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-md",
                      strategy === "vision"
                        ? "bg-surface shadow-sm font-medium"
                        : "text-text-2 hover:text-text"
                    )}
                  >
                    <Eye className="w-3 h-3" /> Vision (Gemini)
                  </button>
                  <span className="ml-auto text-[10px] text-text-3">
                    {strategy === "vision"
                      ? "PDF direkt an Gemini — sieht KPIs/Charts"
                      : "Markdown in Blöcken — billig, ohne Bilder"}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {cols.map((c, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[1fr_100px_2fr_auto] gap-2 items-start bg-surface-2 rounded-md p-2"
                    >
                      <input
                        value={c.name}
                        onChange={(e) =>
                          updateCol(idx, { name: e.target.value })
                        }
                        placeholder="Name"
                        className="text-sm bg-surface border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500/40"
                      />
                      <select
                        value={c.type}
                        onChange={(e) =>
                          updateCol(idx, {
                            type: e.target.value as Column["type"],
                          })
                        }
                        className="text-sm bg-surface border border-border rounded-md px-1 py-1 focus:outline-none"
                      >
                        {TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                      <input
                        value={c.description}
                        onChange={(e) =>
                          updateCol(idx, { description: e.target.value })
                        }
                        placeholder="Beschreibung (optional)"
                        className="text-xs bg-surface border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-500/40"
                      />
                      <button
                        onClick={() => removeCol(idx)}
                        className="p-1.5 text-text-3 hover:text-danger"
                        title="Spalte entfernen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addCol}
                  className="text-xs text-brand-600 hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Spalte hinzufügen
                </button>
              </div>
            )}

            {step === "running" && (
              <div className="py-10 text-center space-y-3">
                <Loader2 className="w-8 h-8 text-brand-500 mx-auto animate-spin" />
                <div className="text-sm font-medium">Extraktion läuft …</div>
                <div className="text-xs text-text-2">
                  Das Modell liest das Dokument und befüllt deine Spalten. Kann
                  bis zu einer Minute dauern.
                </div>
              </div>
            )}

            {step === "done" && (
              <div className="py-8 text-center space-y-3">
                <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
                <div className="text-sm font-medium">
                  Tabelle erstellt — {rowCount ?? 0} Zeile
                  {(rowCount ?? 0) === 1 ? "" : "n"} extrahiert.
                </div>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
            {step === "intent" && (
              <Button variant="ghost" size="md" onClick={onClose}>
                Abbrechen
              </Button>
            )}
            {step === "prompt" && (
              <>
                <Button variant="ghost" size="md" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={inferSchema}
                  disabled={busy || !hint.trim()}
                  loading={busy}
                  icon={<ArrowRight className="w-4 h-4" />}
                >
                  Schema vorschlagen
                </Button>
              </>
            )}
            {step === "schema" && (
              <>
                <Button variant="ghost" size="md" onClick={() => setStep("prompt")}>
                  Zurück
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  onClick={runExtraction}
                  disabled={busy || cols.length === 0}
                >
                  Tabelle erstellen
                </Button>
              </>
            )}
            {step === "done" && (
              <>
                <Button variant="ghost" size="md" onClick={onClose}>
                  Schließen
                </Button>
                <Button variant="primary" size="md" onClick={goToDataset}>
                  Tabelle ansehen
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Stepper({ step }: { step: Step }) {
  const stages: { id: Step; label: string }[] = [
    { id: "intent", label: "Vorschlag" },
    { id: "prompt", label: "Beschreiben" },
    { id: "schema", label: "Spalten" },
    { id: "running", label: "Extrahieren" },
    { id: "done", label: "Fertig" },
  ];
  const idx = stages.findIndex((s) => s.id === step);
  return (
    <div className="px-5 py-2 border-b border-border flex items-center gap-1.5">
      {stages.map((s, i) => (
        <div key={s.id} className="flex items-center gap-1.5">
          <div
            className={clsx(
              "w-5 h-5 rounded-full grid place-items-center text-[10px] font-medium",
              i < idx
                ? "bg-success text-white"
                : i === idx
                  ? "bg-brand-600 text-white"
                  : "bg-surface-3 text-text-2"
            )}
          >
            {i + 1}
          </div>
          <span
            className={clsx(
              "text-[11px]",
              i === idx
                ? "text-text dark:text-white font-medium"
                : "text-text-2"
            )}
          >
            {s.label}
          </span>
          {i < stages.length - 1 && (
            <div className="w-4 h-px bg-surface-3 mx-0.5" />
          )}
        </div>
      ))}
    </div>
  );
}
